import { BookedParty, BookedPartyPaginatedResponse, PartyNote } from '@types';
import {
  ConfirmationModal,
  Pagination,
  PDFViewerModal,
  SearchBar,
} from '_components/atoms';
import { EditPartyModal } from '_components/molecules';
import { useAppSelector } from '_hooks';
import AdminService from '_services/AdminService';
import { ExcelExport, HelperMethods } from '_utils';
import React, { useCallback, useEffect, useState } from 'react';
import { FaReceipt } from 'react-icons/fa';
import { MdClose } from 'react-icons/md';

const BookedParties: React.FC = () => {
  // Get user role from Redux store
  const { accountDetails } = useAppSelector((state: any) => state.auth);
  const userRole = accountDetails?.roleKey;
  const canDelete = ['admin', 'super_admin'].includes(userRole);

  const [parties, setParties] = useState<BookedParty[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState<string | null>(null);
  const [redemptionFilter, setRedemptionFilter] = useState<
    'all' | 'redeemed' | 'not_redeemed'
  >('not_redeemed');
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedPartyId, setSelectedPartyId] = useState<number | null>(null);
  const [partyToDelete, setPartyToDelete] = useState<number | null>(null);
  const [redeemingPartyId, setRedeemingPartyId] = useState<number | null>(null);
  const [deletingPartyId, setDeletingPartyId] = useState<number | null>(null);
  const [expandedPartyIds, setExpandedPartyIds] = useState<Set<number>>(
    new Set(),
  );
  const [pdfViewerModal, setPdfViewerModal] = useState<{
    isOpen: boolean;
    url: string;
    title: string;
  }>({
    isOpen: false,
    url: '',
    title: '',
  });
  const [editModal, setEditModal] = useState<{
    isOpen: boolean;
    party: BookedParty | null;
  }>({
    isOpen: false,
    party: null,
  });
  const [addNoteModal, setAddNoteModal] = useState<{
    isOpen: boolean;
    partyId: number | null;
  }>({
    isOpen: false,
    partyId: null,
  });
  const [noteText, setNoteText] = useState('');
  const [isSubmittingNote, setIsSubmittingNote] = useState(false);
  const [partyNotes, setPartyNotes] = useState<PartyNote[]>([]);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);

  const fetchParties = useCallback(async () => {
    try {
      setLoading(true);

      const responseData = (await AdminService.getBookedParties(
        pageNumber,
        pageSize,
        searchTerm,
        filterDate,
      )) as unknown as BookedPartyPaginatedResponse;

      if (responseData) {
        // Sort parties by session date in chronological order (upcoming dates first)
        let sortedParties = [...responseData.parties].sort((a, b) => {
          const dateA = new Date(a.sessionDate).getTime();
          const dateB = new Date(b.sessionDate).getTime();
          return dateA - dateB;
        });

        // Apply redemption filter
        if (redemptionFilter === 'redeemed') {
          sortedParties = sortedParties.filter(party => party.redeemed);
        } else if (redemptionFilter === 'not_redeemed') {
          sortedParties = sortedParties.filter(party => !party.redeemed);
        }

        setParties(sortedParties);
        setTotalRecords(responseData.pagination.totalRecords);
        setTotalPages(responseData.pagination.totalPages);
        setHasNext(responseData.pagination.hasNext);
        setHasPrevious(responseData.pagination.hasPrevious);
      }
    } catch (error) {
      console.error('Error fetching parties:', error);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm, filterDate, redemptionFilter]);

  useEffect(() => {
    fetchParties();
  }, [fetchParties]);

  const handleSearch = useCallback((searchTerm: string) => {
    // Only search if there are at least 3 characters or the search term is empty (to clear results)
    if (searchTerm.length >= 3 || searchTerm.length === 0) {
      setSearchTerm(searchTerm);
      setPageNumber(1); // Reset to first page when searching
    }
  }, []);

  const handleDateFilterChange = useCallback((date: string | null) => {
    setFilterDate(date);
    setPageNumber(1); // Reset to first page when changing date filter
  }, []);

  const handleRedemptionFilterChange = (
    filter: 'all' | 'redeemed' | 'not_redeemed',
  ) => {
    setRedemptionFilter(filter);
    setPageNumber(1); // Reset to first page when changing redemption filter
  };

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPageNumber(1); // Reset to first page when changing page size
  };

  const handleRedeemParty = (partyId: number) => {
    setSelectedPartyId(partyId);
    setShowConfirmModal(true);
  };

  const partyToRedeem = parties.find(p => p.id === selectedPartyId);
  const redeemNotToday =
    !!partyToRedeem && !HelperMethods.isDateToday(partyToRedeem.sessionDate);

  const confirmRedeemParty = async () => {
    if (!selectedPartyId) return;

    try {
      setRedeemingPartyId(selectedPartyId);
      // The API rejects other-day parties unless staff confirmed the warning below
      await AdminService.redeemBookedParty(selectedPartyId, {
        allowDateMismatch: redeemNotToday,
      });

      // Refresh the data
      await fetchParties();
    } catch (error) {
      console.error('Error redeeming party:', error);
    } finally {
      setRedeemingPartyId(null);
    }
  };

  const openPdfViewer = (url: string, title: string) => {
    setPdfViewerModal({
      isOpen: true,
      url,
      title,
    });
  };

  const closePdfViewer = () => {
    setPdfViewerModal({
      isOpen: false,
      url: '',
      title: '',
    });
  };

  const toggleExpandRow = (partyId: number) => {
    setExpandedPartyIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(partyId)) {
        newSet.delete(partyId);
      } else {
        newSet.add(partyId);
      }
      return newSet;
    });
  };

  const handleDownloadExcel = () => {
    try {
      ExcelExport.exportPartiesToExcel(parties);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
    }
  };

  const handleEditParty = (party: BookedParty) => {
    setEditModal({
      isOpen: true,
      party,
    });
  };

  const handleCloseEditModal = () => {
    setEditModal({
      isOpen: false,
      party: null,
    });
  };

  const handlePartyUpdated = () => {
    fetchParties();
  };

  const fetchPartyNotes = async (partyId: number) => {
    try {
      setIsLoadingNotes(true);
      const notes = await AdminService.getPartyNotes(partyId);
      setPartyNotes(notes as unknown as PartyNote[]);
    } catch (error) {
      console.error('Error fetching party notes:', error);
      setPartyNotes([]);
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const handleAddNote = (partyId: number) => {
    setAddNoteModal({
      isOpen: true,
      partyId,
    });
    setNoteText('');
    fetchPartyNotes(partyId);
  };

  const handleCloseAddNoteModal = () => {
    setAddNoteModal({
      isOpen: false,
      partyId: null,
    });
    setNoteText('');
    setPartyNotes([]);
  };

  const handleSubmitNote = async () => {
    if (!addNoteModal.partyId || !noteText.trim()) return;

    try {
      setIsSubmittingNote(true);
      await AdminService.addBookedPartyNote(
        addNoteModal.partyId,
        noteText.trim(),
      );

      // Clear the note text
      setNoteText('');

      // Refresh the notes list
      const notes = await AdminService.getPartyNotes(addNoteModal.partyId);
      const refreshedNotes = notes as unknown as PartyNote[];
      setPartyNotes(refreshedNotes);

      // Update inline staff notes on the party without a full page reload
      setParties(prev =>
        prev.map(party =>
          party.id === addNoteModal.partyId
            ? { ...party, partyNotes: refreshedNotes }
            : party,
        ),
      );
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setIsSubmittingNote(false);
    }
  };

  const handleDeleteParty = (partyId: number) => {
    setPartyToDelete(partyId);
    setShowDeleteConfirmModal(true);
  };

  const confirmDeleteParty = async () => {
    if (!partyToDelete) return;

    try {
      setDeletingPartyId(partyToDelete);
      await AdminService.deleteBookedParty(partyToDelete);
      console.log('Deleted party:', partyToDelete);

      // Close the modal
      setShowDeleteConfirmModal(false);
      setPartyToDelete(null);

      // Refresh the data
      await fetchParties();
    } catch (error) {
      console.error('Error deleting party:', error);
    } finally {
      setDeletingPartyId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Booked Parties</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage and view all booked parties
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="text-sm text-gray-600">
            Showing: <span className="font-semibold">{parties.length}</span>
            {redemptionFilter !== 'all' && (
              <span className="ml-1 text-gray-500">
                (filtered from {totalRecords} total)
              </span>
            )}
          </div>
          <button
            onClick={handleDownloadExcel}
            disabled={parties.length === 0}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white transition-colors ${
              parties.length === 0
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500'
            }`}
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            Download Excel
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <SearchBar
        placeholder="Search by customer name, email, party name, or barcode ID..."
        onSearch={handleSearch}
        debounceMs={1000}
        filterByDate="sessionDate"
        onDateFilterChange={handleDateFilterChange}
        defaultDateFilter="all"
      />

      {/* Redemption Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-gray-700">
            Redemption Status:
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => handleRedemptionFilterChange('all')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                redemptionFilter === 'all'
                  ? 'bg-blue-600 text-white ring-2 ring-blue-600 ring-offset-1'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Parties
            </button>
            <button
              onClick={() => handleRedemptionFilterChange('redeemed')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                redemptionFilter === 'redeemed'
                  ? 'bg-green-600 text-white ring-2 ring-green-600 ring-offset-1'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Redeemed
              </span>
            </button>
            <button
              onClick={() => handleRedemptionFilterChange('not_redeemed')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                redemptionFilter === 'not_redeemed'
                  ? 'bg-yellow-100 text-yellow-700 ring-2 ring-yellow-200 ring-offset-1 font-bold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <span className="flex items-center gap-1.5">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Not Redeemed
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Pagination Controls */}
      <Pagination
        pageNumber={pageNumber}
        totalPages={totalPages}
        pageSize={pageSize}
        hasNext={hasNext}
        hasPrevious={hasPrevious}
        loading={loading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onReload={fetchParties}
      />

      {/* Parties Table - Desktop */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto max-w-full">
          <table className="w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                  {/* Expand column */}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                  Party Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                  Session Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Redeemed
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/6">
                  Receipt
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider text-center w-1/6">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {parties.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500"
                  >
                    No parties found
                  </td>
                </tr>
              ) : (
                parties.map(party => (
                  <React.Fragment key={party.id}>
                    <tr
                      className={`hover:bg-gray-50 border-t-2 border-gray-300 ${!party.guestName && !party.notes && !(party.partyNotes && party.partyNotes.length > 0) && !expandedPartyIds.has(party.id) ? 'border-b-2 border-b-gray-300' : ''}`}
                    >
                      <td className="px-3 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleExpandRow(party.id)}
                          className="text-gray-500 hover:text-gray-700 focus:outline-none"
                        >
                          <svg
                            className={`w-5 h-5 transform transition-transform ${
                              expandedPartyIds.has(party.id) ? 'rotate-90' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-bold text-gray-900">
                          {party.name}
                        </div>
                        <div className="text-xs text-gray-700">
                          Barcode:{' '}
                          <span className="font-semibold text-blue-600">
                            {party.barcodeId}
                          </span>
                        </div>
                        <div className="text-xs text-gray-700">
                          Created:{' '}
                          <span>
                            {HelperMethods.formatDateTimeLocal(party.createdAt)}
                          </span>
                        </div>
                        {party.receiptUrl === null &&
                          party.receiptNumber &&
                          party.receiptNumber.includes('Bypass') && (
                            <div className="text-gray-500 text-xs text-wrap">
                              {party.receiptNumber}
                            </div>
                          )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-red-900 font-bold">
                          {HelperMethods.formatDate(party.sessionDate)}
                        </div>
                        <div className="text-sm text-gray-900 font-bold">
                          {HelperMethods.formatTime12Hour(party.timeSlot)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {party.customerName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {party.customerEmail}
                        </div>
                        <div className="text-sm text-gray-500">
                          {party.customerPhone}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            party.redeemed
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {party.redeemed ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {party.receiptUrl === null ? (
                          <div className="text-gray-400">
                            <FaReceipt className="w-5 h-5" />
                          </div>
                        ) : (
                          <button
                            onClick={() =>
                              openPdfViewer(party.receiptUrl!, 'View Receipt')
                            }
                            className="text-blue-600 hover:text-blue-900"
                          >
                            <FaReceipt className="w-5 h-5" />
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2 justify-end items-center">
                          <button
                            onClick={() => handleAddNote(party.id)}
                            className="px-3 py-1 text-xs rounded-md bg-purple-500 text-white hover:bg-purple-600 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors"
                          >
                            Staff Notes
                          </button>
                          <button
                            onClick={() => handleEditParty(party)}
                            className="px-3 py-1 text-xs rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleRedeemParty(party.id)}
                            disabled={
                              redeemingPartyId === party.id || party.redeemed
                            }
                            className={`px-3 py-1 text-xs rounded-md transition-colors ${
                              redeemingPartyId === party.id || party.redeemed
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                            }`}
                          >
                            {redeemingPartyId === party.id
                              ? 'Redeeming...'
                              : 'Redeem'}
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => handleDeleteParty(party.id)}
                              disabled={deletingPartyId === party.id}
                              className={`px-3 py-1 text-xs rounded-md transition-colors ${
                                deletingPartyId === party.id
                                  ? 'bg-gray-400 text-white cursor-not-allowed'
                                  : 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                              }`}
                            >
                              {deletingPartyId === party.id
                                ? 'Deleting...'
                                : 'Delete'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Full-width row for Guest Name */}
                    {party.guestName && (
                      <tr>
                        <td colSpan={1} />
                        <td
                          colSpan={6}
                          className={`px-6 py-2 bg-blue-50 border-l-4 border-l-blue-400 ${!party.notes && !(party.partyNotes && party.partyNotes.length > 0) && !expandedPartyIds.has(party.id) ? 'border-b-2 border-b-red-300' : ''}`}
                        >
                          <div className="text-sm font-medium text-blue-800">
                            👤 Guest: {party.guestName}
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Full-width row for Pizza List */}
                    {party.pizzaList && (
                      <tr>
                        <td colSpan={1} />
                        <td
                          colSpan={6}
                          className="px-6 py-2 bg-orange-50 border-l-4 border-l-orange-400 text-sm text-orange-700 font-medium"
                        >
                          🍕 Pizza: {party.pizzaList}
                        </td>
                      </tr>
                    )}
                    {/* Full-width row for Cust. Notes */}
                    {party.notes && (
                      <tr>
                        <td colSpan={1} />
                        <td
                          colSpan={6}
                          className={`px-6 py-2 bg-purple-50 border-l-4 border-l-purple-400 ${!(party.partyNotes && party.partyNotes.length > 0) && !expandedPartyIds.has(party.id) ? 'border-b-2 border-b-red-300' : ''}`}
                        >
                          <div className="text-sm font-medium text-purple-800">
                            📝 Cust. Notes: {party.notes}
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Full-width row for Staff Notes */}
                    {party.partyNotes && party.partyNotes.length > 0 && (
                      <tr>
                        <td colSpan={1} />
                        <td
                          colSpan={6}
                          className={`px-6 py-2 bg-indigo-50 border-l-4 border-l-indigo-400 ${!expandedPartyIds.has(party.id) ? 'border-b-2 border-b-red-300' : ''}`}
                        >
                          <div className="text-sm font-medium text-indigo-800 mb-2">
                            📋 Staff Notes
                          </div>
                          <div className="space-y-2">
                            {party.partyNotes.map(note => (
                              <div
                                key={note.id}
                                className="pb-2 border-b border-indigo-200 last:border-b-0 last:pb-0"
                              >
                                <div className="flex items-center justify-between mb-0.5">
                                  <span className="font-medium text-sm text-indigo-900">
                                    {note.posterName}
                                  </span>
                                  <span className="text-xs text-indigo-500">
                                    {new Date(
                                      note.createdAt,
                                    ).toLocaleDateString('en-US', {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric',
                                      hour: 'numeric',
                                      minute: '2-digit',
                                    })}
                                  </span>
                                </div>
                                <div className="text-sm text-indigo-700">
                                  {note.note}
                                </div>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Expanded Row for Party Extras */}
                    {expandedPartyIds.has(party.id) && (
                      <tr>
                        <td colSpan={1} />
                        <td
                          colSpan={6}
                          className="px-6 py-4 bg-blue-100 border-l-4 border-l-indigo-400 border-b-2 border-b-gray-300"
                        >
                          <div className="space-y-3">
                            <h4 className="text-sm font-semibold text-gray-700">
                              Party Extras
                            </h4>
                            {party.bookedPartyExtras &&
                            party.bookedPartyExtras.length > 0 ? (
                              <div className="bg-white rounded-md border border-gray-200 overflow-hidden">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">
                                        Name
                                      </th>
                                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">
                                        Quantity
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {party.bookedPartyExtras.map(
                                      (extra, index: number) => (
                                        <tr
                                          key={index}
                                          className="hover:bg-gray-50"
                                        >
                                          <td className="px-4 py-2 text-sm text-gray-900">
                                            {extra.name}
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-600">
                                            {extra.quantity ?? 0}
                                          </td>
                                        </tr>
                                      ),
                                    )}
                                  </tbody>
                                </table>
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500 italic">
                                No extras added for this party.
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr className="bg-gray-50/50 border-t-2 border-gray-300">
                      <td colSpan={1} />
                      <td colSpan={6} />
                    </tr>
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Parties Cards - Mobile */}
      <div className="md:hidden space-y-4">
        {parties.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center text-gray-500">
            No parties found
          </div>
        ) : (
          parties.map(party => (
            <div
              key={party.id}
              className="bg-white rounded-lg shadow-sm border border-gray-200 p-4"
            >
              {/* Party Info */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2 flex-1">
                    <button
                      onClick={() => toggleExpandRow(party.id)}
                      className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    >
                      <svg
                        className={`w-5 h-5 transform transition-transform ${
                          expandedPartyIds.has(party.id) ? 'rotate-90' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {party.name}
                    </h3>
                  </div>
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      party.redeemed
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}
                  >
                    {party.redeemed ? 'Redeemed' : 'Not Redeemed'}
                  </span>
                </div>
                <div className="text-sm text-gray-600">Type: {party.type}</div>
                <div className="text-xs text-gray-700 mt-1">
                  Barcode:{' '}
                  <span className="font-semibold text-blue-600">
                    {party.barcodeId}
                  </span>
                </div>
                <div className="text-xs text-gray-700 mt-1">
                  Created:{' '}
                  <span className="font-semibold">
                    {HelperMethods.formatDateTimeLocal(party.createdAt)}
                  </span>
                </div>
              </div>

              {/* Session Details */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Session Details
                </h4>
                <div className="text-sm">
                  <div className="text-red-900 font-bold ">
                    {HelperMethods.formatDate(party.sessionDate)}
                  </div>
                  <div className="text-black font-bold">
                    {HelperMethods.formatTime12Hour(party.timeSlot)}
                  </div>
                  {party.pizzaList && (
                    <div className="text-base font-semibold text-orange-700 bg-orange-100 px-3 py-2 rounded-lg">
                      🍕 Pizza: {party.pizzaList}
                    </div>
                  )}
                  {party.guestName && (
                    <div className="mt-2 text-base font-semibold text-blue-700 bg-blue-100 px-3 py-2 rounded-lg">
                      👤 Guest: {party.guestName}
                    </div>
                  )}
                  {party.notes && (
                    <div className="mt-1 text-base font-semibold text-purple-700 bg-purple-100 px-3 py-2 rounded-lg">
                      📝 Cust. Notes: {party.notes}
                    </div>
                  )}
                  {party.partyNotes && party.partyNotes.length > 0 && (
                    <div className="mt-1 text-base font-semibold text-indigo-700 bg-indigo-100 px-3 py-2 rounded-lg">
                      <div className="mb-2">📋 Staff Notes</div>
                      <div className="space-y-2 font-normal">
                        {party.partyNotes.map(note => (
                          <div
                            key={note.id}
                            className="pb-2 border-b border-indigo-200 last:border-b-0 last:pb-0"
                          >
                            <div className="flex items-center justify-between mb-0.5">
                              <span className="font-semibold text-sm text-indigo-900">
                                {note.posterName}
                              </span>
                              <span className="text-xs text-indigo-500 font-normal">
                                {new Date(note.createdAt).toLocaleDateString(
                                  'en-US',
                                  {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                    hour: 'numeric',
                                    minute: '2-digit',
                                  },
                                )}
                              </span>
                            </div>
                            <div className="text-sm text-indigo-700">
                              {note.note}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Info */}
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Customer
                </h4>
                <div className="text-sm text-gray-600">
                  <div className="font-medium">{party.customerName}</div>
                  <div>{party.customerEmail}</div>
                  <div>{party.customerPhone}</div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {party.receiptUrl && (
                  <button
                    onClick={() =>
                      openPdfViewer(party.receiptUrl!, 'View Receipt')
                    }
                    className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-700 text-sm rounded-md hover:bg-blue-200 transition-colors"
                  >
                    <FaReceipt className="w-5 h-5" />
                  </button>
                )}
                <button
                  onClick={() => handleAddNote(party.id)}
                  className="inline-flex items-center px-3 py-2 bg-purple-500 text-white text-sm rounded-md hover:bg-purple-600 transition-colors"
                >
                  Staff Notes
                </button>
                <button
                  onClick={() => handleEditParty(party)}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                >
                  Edit
                </button>
                {!party.redeemed && (
                  <button
                    onClick={() => handleRedeemParty(party.id)}
                    disabled={redeemingPartyId === party.id}
                    className={`inline-flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      redeemingPartyId === party.id
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2'
                    }`}
                  >
                    {redeemingPartyId === party.id ? 'Redeeming...' : 'Redeem'}
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => handleDeleteParty(party.id)}
                    disabled={deletingPartyId === party.id}
                    className={`inline-flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                      deletingPartyId === party.id
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-red-600 text-white hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:ring-offset-2'
                    }`}
                  >
                    {deletingPartyId === party.id ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </div>

              {/* Redeemed By Info */}
              {party.redeemed && party.redeemedBy && (
                <div className="mt-3 text-xs text-gray-500">
                  Redeemed by: {party.redeemedBy}
                </div>
              )}

              {/* Expanded Section for Party Extras - Mobile */}
              {expandedPartyIds.has(party.id) && (
                <div className="mt-4 pt-4 border-t border-gray-200 -mx-4 px-4 -mb-4 pb-4 bg-blue-100 rounded-b-lg">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">
                    Party Extras
                  </h4>
                  {party.bookedPartyExtras &&
                  party.bookedPartyExtras.length > 0 ? (
                    <div className="space-y-2">
                      {party.bookedPartyExtras.map((extra, index: number) => (
                        <div
                          key={index}
                          className="bg-blue-50 rounded-md p-3 border border-gray-200"
                        >
                          <div className="flex justify-between items-center">
                            <div className="text-sm font-medium text-gray-900">
                              {extra.name}
                            </div>
                            <div className="text-sm font-semibold text-gray-600">
                              Qty: {extra.quantity ?? 0}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic">
                      No extras added for this party.
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={confirmRedeemParty}
        title="Redeem Party"
        message={
          redeemNotToday && partyToRedeem
            ? `This party is for ${HelperMethods.formatDate(partyToRedeem.sessionDate)}, not today. Confirm with the customer before redeeming. This action cannot be undone.`
            : 'Are you sure you want to redeem this party? This action cannot be undone.'
        }
        confirmText={redeemNotToday ? 'Redeem anyway' : 'Redeem'}
        cancelText="Cancel"
        type="warning"
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirmModal}
        onClose={() => {
          setShowDeleteConfirmModal(false);
          setPartyToDelete(null);
        }}
        onConfirm={confirmDeleteParty}
        title="Delete Party"
        message="Are you sure you want to delete this party? This action cannot be undone and will permanently remove all party data."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* PDF Viewer Modal */}
      <PDFViewerModal
        isOpen={pdfViewerModal.isOpen}
        onClose={closePdfViewer}
        url={pdfViewerModal.url}
        title={pdfViewerModal.title}
      />

      {/* Edit Party Modal */}
      <EditPartyModal
        isOpen={editModal.isOpen}
        onClose={handleCloseEditModal}
        party={editModal.party}
        onUpdate={handlePartyUpdated}
      />

      {/* Add Note Modal */}
      {addNoteModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={handleCloseAddNoteModal}
            />

            {/* Modal panel */}
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                {/* Header */}
                <div className="mb-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Staff Notes
                    </h3>
                    <button
                      onClick={handleCloseAddNoteModal}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="Close modal"
                    >
                      <MdClose size={24} />
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">
                    View existing staff notes or add a new note for this party
                    booking.
                  </p>
                </div>

                {/* Existing Notes */}
                <div className="mb-4">
                  {/* <h4 className="text-sm font-medium text-gray-900 mb-2">
                    Existing Notes
                  </h4> */}
                  {isLoadingNotes ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    </div>
                  ) : partyNotes.length > 0 ? (
                    <div className="max-h-48 overflow-y-auto space-y-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
                      {partyNotes.map((note: PartyNote) => (
                        <div
                          key={note.id}
                          className="pb-3 border-b border-gray-200 last:border-b-0 last:pb-0"
                        >
                          <div className="flex items-center justify-between mb-1">
                            <div className="font-medium text-sm text-gray-900">
                              {note.posterName}
                            </div>
                            <div className="text-xs text-gray-400">
                              {new Date(note.createdAt).toLocaleDateString(
                                'en-US',
                                {
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                },
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-gray-700">
                            {note.note}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 italic py-2">
                      No staff notes yet for this party.
                    </div>
                  )}
                </div>

                {/* Note Input */}
                <div className="mb-4">
                  <label
                    htmlFor="noteText"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Note
                  </label>
                  <textarea
                    id="noteText"
                    rows={5}
                    value={noteText}
                    onChange={e => setNoteText(e.target.value)}
                    placeholder="Enter your note here..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {noteText.length} characters
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={handleCloseAddNoteModal}
                    disabled={isSubmittingNote}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitNote}
                    disabled={isSubmittingNote || !noteText.trim()}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmittingNote ? 'Posting...' : 'Post Note'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookedParties;
